import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";
import SeoHead from "@/components/SeoHead";
import JsonLd, { buildBreadcrumbSchema, buildWebPageSchema } from "@/components/JsonLd";

const SLUG_META = {
  terms:    { eyebrow: "Fine print",  subtitle: "The terms that govern your use of rieave.com." },
  privacy:  { eyebrow: "Your trust",  subtitle: "How we collect, use and protect your information." },
  returns:  { eyebrow: "No worries",  subtitle: "If a piece isn't right, here's exactly how we'll make it right." },
};

const PageView = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const meta = SLUG_META[slug] || { eyebrow: "Details", subtitle: "" };

  useEffect(() => {
    let mounted = true;
    setData(null);
    setNotFound(false);
    api.get(`/pages/${slug}`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setNotFound(true); });
    return () => { mounted = false; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center py-32" data-testid="page-notfound">
        <p className="eyebrow mb-3">404</p>
        <h1 className="font-hero text-4xl mb-3">Page not found</h1>
        <Link to="/" className="btn-link mt-4">Back to home →</Link>
      </div>
    );
  }

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-mauve">Loading…</div>;

  const sections = data.sections || [];
  const labelMap = { terms: "Terms of Service", privacy: "Privacy Policy", returns: "Returns" };
  const pageUrl = typeof window !== "undefined" ? window.location.origin + `/${slug}` : `/${slug}`;
  const pageTitle = data.seo_title || data.title || labelMap[slug] || "Page";

  return (
    <div className="font-body text-charcoal bg-cream" data-testid={`page-view-${slug}`}>
      <SeoHead
        title={pageTitle}
        description={data.seo_description || data.intro || meta.subtitle}
        keywords={data.seo_keywords}
        image={data.og_image}
        canonical={pageUrl}
        robots={data.seo_robots}
      />
      <JsonLd
        data={[
          buildBreadcrumbSchema([
            { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { name: labelMap[slug] || slug, url: pageUrl },
          ]),
          buildWebPageSchema({
            name: pageTitle,
            description: data.seo_description || data.intro,
            url: pageUrl,
          }),
        ].filter(Boolean)}
      />
      {/* Hero */}
      <section className="border-b border-charcoal/8" data-testid="pageview-hero">
        <div className="rieave-container pt-28 md:pt-36 pb-14 md:pb-20 max-w-3xl">
          <motion.p className="eyebrow mb-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {meta.eyebrow}
          </motion.p>
          <motion.h1
            className="font-hero text-5xl md:text-7xl leading-[0.98] mb-5"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          >
            {data.title}
          </motion.h1>
          <motion.p className="text-lg text-charcoal/65" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            {data.intro || meta.subtitle}
          </motion.p>
          {data.updated_at && (
            <p className="mt-6 text-xs uppercase tracking-[0.22em] text-mauve">Last updated · {new Date(data.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="rieave-container py-16 md:py-24 grid md:grid-cols-12 gap-12 relative">
        {/* Sticky TOC */}
        {sections.length > 0 && (
          <aside className="hidden md:block md:col-span-3 sticky top-24 self-start" data-testid="pageview-toc">
            <p className="eyebrow mb-4">On this page</p>
            <nav className="space-y-2">
              {sections.map((s, i) => (
                <a key={i} href={`#section-${i}`} className="block text-sm text-charcoal/60 hover:text-rose transition border-l border-charcoal/10 hover:border-rose pl-3 py-1">
                  {s.heading}
                </a>
              ))}
            </nav>
          </aside>
        )}
        <div className={sections.length > 0 ? "md:col-span-9 max-w-3xl" : "md:col-span-12 max-w-3xl mx-auto"}>
          {sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <section id={`section-${i}`} className="mb-12 pb-12 border-b border-charcoal/8 last:border-b-0">
                <h2 className="font-hero text-3xl md:text-4xl mb-4">{s.heading}</h2>
                <p className="text-[17px] leading-[1.75] text-charcoal/75 whitespace-pre-line">{s.body}</p>
              </section>
            </Reveal>
          ))}
          <div className="flex items-center gap-5 mt-6">
            <Link to="/contact" className="btn-primary" data-testid="pageview-contact-cta">Have a question?</Link>
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="btn-link" data-testid="pageview-top">Back to top <ArrowUp size={14} /></button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PageView;
